import Debug from 'debug';
import { Dispatch } from 'redux';
import { createAction, createReducer } from '@reduxjs/toolkit';
import { Primitive, Entity } from 'gossamer-unified';

const debug = Debug('gfe/da/ReduxGCache');

// types
export interface GCacheState {
  records: Record<string, GCacheRecord<object>>;
  queries: Record<string, GCacheQuery<object>>;
}

export type GCacheStore = { dispatch: Dispatch; getState: () => { cache: GCacheState } };

export interface GCacheQuery<T> {
  entityId: string;
  query: string;
  data: T[] | undefined;
  error: string | undefined;
  isFetching: boolean;
  expiresAt: number;
}

export interface GCacheRecord<T> {
  entityId: string;
  key: string;
  data: T | undefined;
  error: string | undefined;
  isFetching: boolean;
  expiresAt: number;
}

// initial state
const INITIAL_STATE: GCacheState = {
  records: {},
  queries: {},
};

// Actions

export interface AddRecordPayload {
  entityId: string;
  key: string;
}

export const addRecordToGCache = createAction<AddRecordPayload>('GCache/addRecord');

export interface SetRecordDataPayload {
  entityId: string;
  key: string;
  data: Record<string, Primitive>;
  expiresAt: number;
}

export const setRecordDataInGCache = createAction<SetRecordDataPayload>('GCache/setRecordData');

export interface SetRecordErrorPayload {
  entityId: string;
  key: string;
  error: string;
}

export const setRecordErrorInGCache = createAction<SetRecordErrorPayload>('GCache/setRecordError');

export interface GetRecordPayload {
  entity: Entity<any>;
  key: string;
}

export const getRecordFromGCache = createAction<GetRecordPayload>('GCache/getRecord');

export interface ClearSingleRecordPayload {
  entityId: string;
  key: string;
}

export const clearSingleRecordInGCache = createAction<ClearSingleRecordPayload>('GCache/clearSingleRecord');

export interface ClearAllRecordsForEntityPayload {
  entityId: string;
}

export const clearAllRecordsForEntityInGCache = createAction<ClearAllRecordsForEntityPayload>(
  'GCache/clearAllRecordsForEntity',
);

export interface SetAllFetchingRecordsAsNotFoundPayload {
  entityId: string;
}

export const setAllFetchingRecordsAsNotFound = createAction<SetAllFetchingRecordsAsNotFoundPayload>(
  'GCache/setAllFetchingRecordsAsNotFound',
);

export interface AddQueryPayload {
  entityId: string;
  query: string;
}

export const addQueryToGCache = createAction<AddQueryPayload>('GCache/addQuery');

export interface SetQueryDataPayload {
  entityId: string;
  query: string;
  data: Record<string, Primitive>[];
  keys: string[];
  expiresAt: number;
}

export const setQueryDataInGCache = createAction<SetQueryDataPayload>('GCache/setQueryData');

interface SetQueryErrorPayload {
  entityId: string;
  query: string;
  error: string;
}

export const setQueryErrorInGCache = createAction<SetQueryErrorPayload>('GCache/setQueryError');

export interface GetQueryPayload {
  entity: Entity<any>;
  query: any;
}

export const getQueryFromGCache = createAction<GetQueryPayload>('GCache/getQuery');

export interface ClearSingleQueryPayload {
  entityId: string;
  query: string;
}

export const clearSingleQueryInGCache = createAction<ClearSingleQueryPayload>('GCache/clearSingleQuery');

interface ClearAllQueriesForEntityPayload {
  entityId: string;
}

export const clearAllQueriesForEntityInGCache = createAction<ClearAllQueriesForEntityPayload>(
  'GCache/clearAllQueriesForEntity',
);

export interface ClearPayload {}

export const clearGCache = createAction<ClearPayload>('GCache/clear');

export const cacheSweep = createAction<{}>('GCache/cacheSweep');

export const makeRecordGCacheKey = (entityId: string, key: string) => [entityId, key].join('|');

export const makeQueryGCacheKey = (entityId: string, query: string) => [entityId, query].join('|');

export const getDefaultGCacheRecordEntry = <T>(entityId: string, key: string): GCacheRecord<T> => ({
  entityId,
  key,
  data: undefined,
  error: undefined,
  isFetching: false,
  expiresAt: Number.POSITIVE_INFINITY,
});

export const getDefaultGCacheQueryEntry = <T>(entityId: string, query: string): GCacheQuery<T> => ({
  entityId,
  query,
  data: undefined,
  error: undefined,
  isFetching: false,
  expiresAt: Number.POSITIVE_INFINITY,
});

export const getSingleRetrievalQuery = () => ({});

export const cacheReducer = createReducer(INITIAL_STATE, (builder) => {
  builder

    .addCase(addRecordToGCache, (state, action) => {
      const entry: GCacheRecord<{}> = getDefaultGCacheRecordEntry(action.payload.entityId, action.payload.key);
      const singleRetrievalQuery =
        state.queries[makeQueryGCacheKey(action.payload.entityId, JSON.stringify(getSingleRetrievalQuery()))];
      if (singleRetrievalQuery?.data) {
        // all data already loaded in a single retrieval
        entry.error = 'not found';
        entry.isFetching = false;
      } else {
        entry.isFetching = true;
      }
      debug(`addRecordToGCache(${action.payload.entityId}, ${action.payload.key})`, entry);
      state.records[makeRecordGCacheKey(action.payload.entityId, action.payload.key)] = entry;
    })

    .addCase(setRecordDataInGCache, (state, action) => {
      const cacheKey = makeRecordGCacheKey(action.payload.entityId, action.payload.key);
      const entry: GCacheRecord<{}> = (state.records[cacheKey] =
        state.records[cacheKey] || getDefaultGCacheRecordEntry(action.payload.entityId, action.payload.key));
      debug(`setRecordDataInGCache()`, cacheKey, action.payload.data);
      entry.data = action.payload.data;
      entry.expiresAt = action.payload.expiresAt;
      entry.isFetching = false;
    })

    .addCase(setRecordErrorInGCache, (state, action) => {
      debug(`setRecordErrorInGCache(${action.payload.entityId}, ${action.payload.key})`);
      const entry: GCacheRecord<{}> = state.records[makeRecordGCacheKey(action.payload.entityId, action.payload.key)];
      entry.error = action.payload.error;
      entry.isFetching = false;
    })

    .addCase(clearSingleRecordInGCache, (state, action) => {
      debug(`clearSingleRecordInGCache(${action.payload.entityId}, ${action.payload.key})`);
      delete state.records[makeRecordGCacheKey(action.payload.entityId, action.payload.key)];
    })

    .addCase(clearAllRecordsForEntityInGCache, (state, action) => {
      debug(`clearAllRecordsForEntityInGCache(${action.payload.entityId})`);
      const cacheKeys = Object.keys(state.records);
      cacheKeys
        .filter((cacheKey) => cacheKey.indexOf(action.payload.entityId + '|') === 0)
        .forEach((cacheKey) => delete state.records[cacheKey]);
    })

    .addCase(setAllFetchingRecordsAsNotFound, (state, action) => {
      debug(`setAllFetchingRecordsAsNotFound(${action.payload.entityId})`);
      Object.values(state.records)
        .filter((record) => record.entityId === action.payload.entityId && record.isFetching)
        .forEach((record) => {
          record.isFetching = false;
          record.error = 'not found';
        });
    })

    .addCase(addQueryToGCache, (state, action) => {
      const entry: GCacheQuery<{}> = getDefaultGCacheQueryEntry(action.payload.entityId, action.payload.query);
      debug(`addQueryToGCache(${action.payload.entityId}, ${action.payload.query})`, entry);
      state.queries[makeQueryGCacheKey(action.payload.entityId, action.payload.query)] = entry;
      entry.isFetching = true;
    })

    .addCase(setQueryDataInGCache, (state, action) => {
      const cacheKey = makeQueryGCacheKey(action.payload.entityId, action.payload.query);
      const entry: GCacheQuery<{}> = (state.queries[cacheKey] =
        state.queries[cacheKey] || getDefaultGCacheQueryEntry(action.payload.entityId, action.payload.query));
      debug(`setQueryDataInGCache()`, cacheKey, entry);
      entry.data = action.payload.data;
      entry.expiresAt = action.payload.expiresAt;
      entry.isFetching = false;

      entry.data.forEach((row, index) => {
        const key = action.payload.keys[index];
        const cacheKey = makeRecordGCacheKey(action.payload.entityId, key);
        const entry: GCacheRecord<{}> = (state.records[cacheKey] =
          state.records[cacheKey] || getDefaultGCacheRecordEntry(action.payload.entityId, key));
        entry.data = row;
        entry.expiresAt = action.payload.expiresAt; // TODO should be based on record expiry really
        entry.isFetching = false;
      });
    })

    .addCase(setQueryErrorInGCache, (state, action) => {
      debug(`setQueryErrorInGCache(${action.payload.entityId}, ${action.payload.query})`);
      const entry: GCacheQuery<{}> = state.queries[makeQueryGCacheKey(action.payload.entityId, action.payload.query)];
      entry.error = action.payload.error;
      entry.isFetching = false;
    })

    .addCase(clearSingleQueryInGCache, (state, action) => {
      debug(`clearSingleQueryInGCache(${action.payload.entityId}, ${action.payload.query})`);
      state.queries[makeQueryGCacheKey(action.payload.entityId, action.payload.query)].expiresAt = 0;
    })

    .addCase(clearAllQueriesForEntityInGCache, (state, action) => {
      debug(`clearAllQueriesForEntityInGCache(${action.payload.entityId})`);
      const cacheKeys = Object.keys(state.queries);
      cacheKeys
        .filter((cacheKey) => cacheKey.indexOf(action.payload.entityId + '|') === 0)
        .forEach((cacheKey) => (state.queries[cacheKey].expiresAt = 0));
    })

    .addCase(clearGCache, (state, action) => {
      debug(`clearGCache()`);
      state.records = {};
      state.queries = {};
    });
});

// Selectors
export const selectRecordFromGCache = (state: { cache: GCacheState }, entityId: string, key: string) =>
  state.cache.records[makeRecordGCacheKey(entityId, key)];

export const selectQueryFromGCache = (state: { cache: GCacheState }, entityId: string, query: string) =>
  state.cache.queries[makeQueryGCacheKey(entityId, query)];
